<template>
  <el-dialog :title="$t('splitRangeFee.title')" :visible.sync="splitRangeFeeInfo.show" width="480px"
    :close-on-click-modal="false" @closed="reset">
    <div v-if="!splitRangeFeeInfo.payFail">
      <el-form label-width="110px">
        <el-form-item :label="$t('splitRangeFee.billRange')">
          <span>{{ splitRangeFeeInfo.billStart }} ~ {{ splitRangeFeeInfo.billEnd }}</span>
        </el-form-item>
        <el-form-item :label="$t('splitRangeFee.range')">
          <div style="display: flex; align-items: center;">
            <el-date-picker v-model="splitRangeFeeInfo.rangeStart" type="date" value-format="yyyy-MM-dd"
              :placeholder="$t('splitRangeFee.rangeStart')" :picker-options="startPickerOptions"
              style="flex: 1;" @change="estimate"></el-date-picker>
            <span style="margin: 0 6px; color: #909399;">~</span>
            <el-date-picker v-model="splitRangeFeeInfo.rangeEnd" type="date" value-format="yyyy-MM-dd"
              :placeholder="$t('splitRangeFee.rangeEnd')" :picker-options="endPickerOptions"
              style="flex: 1;" @change="estimate"></el-date-picker>
          </div>
        </el-form-item>
        <el-form-item :label="$t('splitRangeFee.receivable')">
          <span>￥{{ splitRangeFeeInfo.estAmount }}</span>
        </el-form-item>
        <el-form-item :label="$t('splitRangeFee.received')">
          <el-input v-model="splitRangeFeeInfo.receivedAmount" type="number" min="0"
            :placeholder="$t('splitRangeFee.receivedPlaceholder')" style="width: 100%"></el-input>
        </el-form-item>
        <el-form-item :label="$t('splitRangeFee.payMethod')">
          <el-select v-model="splitRangeFeeInfo.primeRate" style="width: 100%">
            <template v-for="item in splitRangeFeeInfo.primeRates">
              <el-option v-if="item.statusCd != '5' && item.statusCd != '6' && item.statusCd != '8'"
                :key="item.statusCd" :label="item.name" :value="item.statusCd"></el-option>
            </template>
          </el-select>
        </el-form-item>
      </el-form>
    </div>
    <div v-else>
      <el-alert type="warning" :closable="false"
        :title="$t('splitRangeFee.splitOkPayFail')"
        :description="$t('splitRangeFee.splitOkPayFailDesc')"></el-alert>
    </div>
    <div slot="footer" class="dialog-footer">
      <el-button size="small" @click="splitRangeFeeInfo.show = false">{{ $t('splitRangeFee.cancel') }}</el-button>
      <el-button v-if="!splitRangeFeeInfo.payFail" type="primary" size="small" :loading="splitRangeFeeInfo.loading"
        @click="submit">{{ $t('splitRangeFee.confirm') }}</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { splitFee } from '@/api/fee/simplifyRoomFeeApi'
import { payFee, listFeePrintPages, queryFeeReceipt } from '@/api/fee/payFeeOrderApi'
import { getDict, getCommunityId } from '@/api/community/communityApi'

export default {
  name: 'SplitRangeFee',
  data() {
    return {
      splitRangeFeeInfo: {
        show: false,
        loading: false,
        payFail: false,
        feeId: '',
        amount: '',
        billStart: '',
        billEnd: '',
        billStartDate: null,
        billEndDate: null,
        rangeStart: '',
        rangeEnd: '',
        estAmount: '0.00',
        receivedAmount: '',
        primeRate: '',
        primeRates: []
      },
      // 开始日期：仅允许在账单区间内选择
      startPickerOptions: {
        disabledDate: (time) => {
          if (!this.splitRangeFeeInfo.billStartDate) return true
          return time.getTime() < this.splitRangeFeeInfo.billStartDate.getTime() ||
            time.getTime() > this.splitRangeFeeInfo.billEndDate.getTime()
        }
      },
      // 结束日期：账单区间内，且不得早于已选的开始日期
      endPickerOptions: {
        disabledDate: (time) => {
          if (!this.splitRangeFeeInfo.billStartDate) return true
          if (time.getTime() < this.splitRangeFeeInfo.billStartDate.getTime() ||
            time.getTime() > this.splitRangeFeeInfo.billEndDate.getTime()) {
            return true
          }
          const start = this.splitRangeFeeInfo.rangeStart
          if (start) {
            return time.getTime() < new Date(start.replace(/-/g, '/')).getTime()
          }
          return false
        }
      }
    }
  },
  methods: {
    open(fee) {
      // fee 为 simplifyRoomFee 账单行对象
      // fee.startTime/endTime 为字符串（可能含时分秒），账单区间展示与 Date 构造均取日期部分
      const info = this.splitRangeFeeInfo
      info.payFail = false
      info.feeId = fee.feeId
      info.amount = fee.amount
      info.billStart = (fee.startTime || '').split(' ')[0]
      info.billEnd = (fee.endTime || '').split(' ')[0]
      info.billStartDate = new Date(info.billStart.replace(/-/g, '/'))
      info.billEndDate = new Date(info.billEnd.replace(/-/g, '/'))
      info.rangeStart = ''
      info.rangeEnd = ''
      info.estAmount = '0.00'
      info.receivedAmount = ''
      info.primeRate = ''
      info.show = true
      // 收款方式字典与 payFeeOrderList.vue 保持一致
      getDict('pay_fee_detail', 'prime_rate').then(data => {
        info.primeRates = data
      })
    },
    estimate() {
      // 预估应收=账单金额×段天数/当月自然天数（含头含尾），最终以 splitFee 返回 seg2Amount 为准
      const info = this.splitRangeFeeInfo
      if (!info.rangeStart || !info.rangeEnd) {
        info.estAmount = '0.00'
        return
      }
      const billAmount = parseFloat(info.amount)
      if (isNaN(billAmount)) {
        // 账单金额非数字时兜底，避免显示 ￥NaN
        info.estAmount = '0.00'
        info.receivedAmount = ''
        return
      }
      const p = new Date(info.rangeStart.replace(/-/g, '/'))
      const q = new Date(info.rangeEnd.replace(/-/g, '/'))
      if (p > q) {
        info.estAmount = '0.00'
        return
      }
      const days = Math.round((q - p) / 86400000) + 1
      const monthDays = new Date(p.getFullYear(), p.getMonth() + 1, 0).getDate()
      const est = (billAmount * days / monthDays).toFixed(2)
      info.estAmount = est
      info.receivedAmount = est
    },
    async submit() {
      const info = this.splitRangeFeeInfo
      if (!info.rangeStart || !info.rangeEnd) {
        this.$message.error(this.$t('splitRangeFee.rangeRequired'))
        return
      }
      if (new Date(info.rangeStart.replace(/-/g, '/')) > new Date(info.rangeEnd.replace(/-/g, '/'))) {
        this.$message.error(this.$t('splitRangeFee.rangeOrderInvalid'))
        return
      }
      const received = parseFloat(info.receivedAmount)
      if (isNaN(received) || received < 0) {
        this.$message.error(this.$t('splitRangeFee.receivedInvalid'))
        return
      }
      if (received > parseFloat(info.estAmount)) {
        // 实收>应收前端拦截（后端 -103 分支不拦截，属既有坑，本期前端兜底）
        this.$message.error(this.$t('splitRangeFee.receivedOver'))
        return
      }
      if (!info.primeRate) {
        this.$message.error(this.$t('splitRangeFee.payMethodRequired'))
        return
      }
      info.loading = true
      try {
        // ① 拆账
        const splitRes = await splitFee({
          feeId: info.feeId,
          rangeStart: info.rangeStart,
          rangeEnd: info.rangeEnd
        })
        if (!splitRes || parseInt(splitRes.code) !== 0) {
          this.$message.error(splitRes ? splitRes.msg : this.$t('splitRangeFee.splitFail'))
          return
        }
        // data 为 null/undefined 时兜底，避免 TypeError 落入外层 catch 误报"拆账失败"
        const splitData = splitRes.data || {}
        const seg2FeeId = splitData.seg2FeeId
        const seg2Amount = splitData.seg2Amount
        if (!seg2FeeId) {
          // 拆账结果不明确（拆账可能已成功），转提示态，由用户到列表核对后处理
          info.payFail = true
          return
        }
        // ② 对缴费段全额缴（-103）；totalFeePrice/selectDiscount 为 /fee.payFee 既有契约（对齐 payFeeOrderConfirm）
        try {
          const payRes = await payFee({
            feeId: seg2FeeId,
            cycles: '-103',
            custEndTime: info.rangeEnd + ' 23:59:59',
            receivableAmount: seg2Amount,
            receivedAmount: received.toFixed(2),
            totalFeePrice: seg2Amount,
            primeRate: info.primeRate,
            selectDiscount: '[]'
          })
          if (!payRes || parseInt(payRes.code) !== 0) {
            throw new Error(payRes ? payRes.msg : 'pay fail')
          }
          info.show = false
          this.$message.success(this.$t('splitRangeFee.success'))
          this.$emit('success')
          // 与正常缴费一致：自动查询收据并打开打印页（打印页 URL 取小区配置变体）
          this.printReceipt(payRes.data)
        } catch (e) {
          // ①成功②失败：拆账结果保留，转为提示态
          info.payFail = true
        }
      } catch (e) {
        this.$message.error(this.$t('splitRangeFee.splitFail'))
      } finally {
        info.loading = false
      }
    },
    reset() {
      // payFail 态关闭弹窗时通知父级刷新（账单形态已变）；
      // 成功路径已在 submit 内 emit 过一次，此处 payFail=false 不会重复触发
      if (this.splitRangeFeeInfo.payFail) {
        this.$emit('success')
      }
      this.splitRangeFeeInfo.payFail = false
    },
    // 缴费成功后自动打开收据打印页（对齐正常缴费的 autoPrint 行为；
    // 打印页 URL 按小区配置解析 printPayFee 变体）。任何失败静默降级，不影响已成功缴费。
    async printReceipt(payData) {
      try {
        const detailId = payData && payData.detailId
        if (!detailId) {
          return
        }
        const communityId = await getCommunityId()
        const receiptRes = await queryFeeReceipt({
          detailIds: detailId,
          communityId: communityId,
          page: 1,
          row: 1
        })
        if (!receiptRes || parseInt(receiptRes.code) !== 0
          || !receiptRes.data || receiptRes.data.length === 0) {
          return
        }
        const receiptId = receiptRes.data[0].receiptId
        let printUrl = '/#/pages/property/printPayFee'
        try {
          const pageRes = await listFeePrintPages({ page: 1, row: 1, state: 'T', communityId: communityId })
          if (pageRes && pageRes.data && pageRes.data.length > 0) {
            printUrl = pageRes.data[0].url
          }
        } catch (e) {
          // 取不到小区打印页配置时使用默认打印页
        }
        window.open(printUrl + '?receiptId=' + receiptId + '&merge=OFF')
      } catch (e) {
        // 收据查询失败静默：缴费已成功，打印可从缴费历史重新发起
      }
    }
  }
}
</script>
